import React, { memo } from 'react';
import styled from 'styled-components';
import upperFirst from 'lodash/upperFirst';

import colors from '@front/common/src/utils/colors';
import { getCurrentCountry } from '@front/common/src/utils/country';

import { SinglePrestationsPricesCleaningProps } from '@front/customer/src/components/basket/types';
import { getFormattedPrice } from '@front/customer/src/utils/cleaning';
import { CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';

import { translate } from '@front/i18n';

import Accordion from '@front/web-ui/src/components/accordion/accordion';
import Separator from '@front/web-ui/src/components/separator/separator';
import Text from '@front/web-ui/src/components/base-text/variants/text';

import PromoCode from '../../../promo-code/promo-code';
import useBasketSection from '../../../../hooks/use-basket-section';
import { route } from '../../../../steps/steps';
import Category from '../common/category';
import Surcharges from '../common/surcharges';
import {
  prestationPricesTitlePreview,
  subtitle,
  usePrestationPriceAlerts,
} from '../common/accordion/prestation-prices';
import { PriceLines } from '../common/price-line/price-lines';

import SinglePricesWithTax from './single-prices-with-tax';

const t = translate('basket.cleaning.services');

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 20px;
`;

const StyledAccordion = styled(Accordion)`
  margin-top: 20px;
`;

const SinglePrestationsPrices = ({
  cleaningPrestation,
  cleaningPrestationOptions,
  duration,
  hasPets,
  isITCSelectedOrSuccessfullyPaired,
  isLoadingPrices,
  mode,
  prices,
  promotion,
  shouldShowPromoCode,
  universeReference,
  user,
}: SinglePrestationsPricesCleaningProps) => {
  const { hasTaxCreditInBasket } = CONFIG_BY_COUNTRY[getCurrentCountry()];

  const hasPrestations = !!cleaningPrestation;
  const hasDuration = !!duration;
  const hasTaxCredit = prices && prices.tax_credit.price > 0;
  const formattedPriceAfterTax = getFormattedPrice(hasDuration, prices?.after_tax);
  const formattedTotalPrice = getFormattedPrice(hasDuration, prices?.total);

  const { isOpen, isVisible } = useBasketSection({
    data: hasPrestations,
    sectionLocations: [
      route('prestations'),
      route('frequency'),
      route('duration'),
      route('options'),
      route('animals'),
    ],
  });

  const alert = usePrestationPriceAlerts();

  if (!isVisible) return null;

  return (
    <Container>
      <Separator />
      <StyledAccordion
        testId='prestations-prices-accordion'
        icon='basket'
        iconLabel={t('basket.services.icon_label.basket')}
        title={upperFirst(t('universes.cleaning.title'))}
        titlePreview={prestationPricesTitlePreview({
          formattedPrice: formattedTotalPrice,
          isITCSelectedOrSuccessfullyPaired,
          isLoadingPrices,
        })}
        subtitle={subtitle({
          formattedPriceAfterTax,
          hasTaxCredit,
          hasTaxCreditInBasket,
          isITCSelectedOrSuccessfullyPaired,
          isLoadingPrices,
        })}
        subtitleVisibility='close'
        isOpen={isOpen}
      >
        {hasPrestations ? (
          <>
            <Category
              cleaningPrestation={cleaningPrestation}
              cleaningPrestationOptions={cleaningPrestationOptions}
              duration={duration}
              hasPets={hasPets}
              frequencyText={t('.frequency.spot')}
            />
            <Surcharges surcharges={prices?.surcharges} />
            <SinglePricesWithTax hasDuration={hasDuration} prices={prices}>
              <PriceLines
                duration={duration}
                isITCSelectedOrSuccessfullyPaired={isITCSelectedOrSuccessfullyPaired}
                isLoading={isLoadingPrices}
                isMultiple={false}
                prices={prices}
              />
            </SinglePricesWithTax>
          </>
        ) : (
          <Text.SM color={colors.ink50}>{t('.no_services')}</Text.SM>
        )}
      </StyledAccordion>
      {alert}
      {mode === 'creation' && hasPrestations && shouldShowPromoCode && (
        <PromoCode
          cleaningPrestation={cleaningPrestation}
          promotion={promotion}
          totalPrice={prices?.total.price || 0}
          universeReference={universeReference}
          user={user}
        />
      )}
    </Container>
  );
};

export default memo(SinglePrestationsPrices);
